import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  IconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import { supabase } from "services/supabase";
import Transitions from "components/Transitions";
import SettingsModal from "components/SettingsModal";

const ProfileMenu = () => {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const anchorRef = useRef(null);
  const { currentUser } = useSupabaseAuth();
  const navigate = useNavigate();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLogout = async () => {
    setOpen(false);
    await supabase.auth.signOut();
    navigate("/login");
  };

  const getInitials = (email) => {
    if (!email) return "A";
    const parts = email.split("@")[0];
    return parts.charAt(0).toUpperCase();
  };

  // Get avatar URL from user_metadata if available
  const avatarUrl = currentUser?.user_metadata?.avatar_url || currentUser?.user_metadata?.picture || null;

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          ref={anchorRef}
          onClick={handleToggle}
          sx={{ p: 0 }}
          aria-controls={open ? "profile-grow" : undefined}
          aria-haspopup="true"
        >
          <Avatar src={avatarUrl || undefined} sx={{ width: 32, height: 32 }}>
            {!avatarUrl && getInitials(currentUser?.email)}
          </Avatar>
        </IconButton>
      </Box>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: 3,
                minWidth: 180,
                maxWidth: 356,
                width: "100%",
                [({ theme }) => theme.breakpoints.down("sm")]: {
                  maxWidth: 250,
                },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <List component="nav" sx={{ p: 0 }}>
                  <ListItemButton disabled>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {currentUser?.email}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          UID: {currentUser?.id?.substring(0, 8)}...
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  <ListItemButton onClick={() => { setOpen(false); setSettingsOpen(true); }}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Settings" />
                  </ListItemButton>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                  </ListItemButton>
                </List>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};

export default ProfileMenu;
